import * as React from 'react';

import { useMediaQuery, useTheme as useMuiTheme } from '@mui/material';

export const SIDEBAR_WIDTH_EXPANDED = 256;
export const SIDEBAR_WIDTH_COLLAPSED = 56;
// Published on the layout root so that other fixed/absolute surfaces
// (entity drawers, global search popup, ...) can follow the sidebar width.
export const SIDEBAR_CSS_VAR = '--app-sidebar-width';

const STORAGE_KEY = 'crm.sidebar.mode';

type SidebarMode = 'expanded' | 'collapsed';

const readStoredMode = (): SidebarMode => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'collapsed'
      ? 'collapsed'
      : 'expanded';
  } catch {
    return 'expanded';
  }
};

type SidebarState = {
  /** Desktop preference, persisted in localStorage */
  mode: SidebarMode;
  toggleMode: () => void;
  isMobile: boolean;
  /** Mobile temporary drawer visibility, transient */
  mobileOpen: boolean;
  openMobile: () => void;
  closeMobile: () => void;
  /** Current width reserved by the sidebar: 256, 56 or 0 (mobile) */
  sidebarWidth: number;
};

const SidebarStateContext = React.createContext<SidebarState | null>(null);

export const useSidebarState = (): SidebarState => {
  const context = React.useContext(SidebarStateContext);
  if (context == null) {
    throw Error('<SidebarStateProvider> should be in the tree');
  }
  return context;
};

export const SidebarStateProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { breakpoints } = useMuiTheme();
  const isMobile = useMediaQuery(breakpoints.down('md'));
  const [mode, setMode] = React.useState<SidebarMode>(readStoredMode);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const toggleMode = React.useCallback(() => {
    setMode(prev => {
      const next = prev === 'expanded' ? 'collapsed' : 'expanded';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // private mode or storage full — keep the in-memory state only
      }
      return next;
    });
  }, []);

  const openMobile = React.useCallback(() => setMobileOpen(true), []);
  const closeMobile = React.useCallback(() => setMobileOpen(false), []);

  // discard the transient drawer state when leaving mobile, so the drawer
  // does not unexpectedly reappear after resizing back below the breakpoint
  React.useEffect(() => {
    if (!isMobile) {
      setMobileOpen(false);
    }
  }, [isMobile]);

  const sidebarWidth = isMobile
    ? 0
    : mode === 'expanded'
    ? SIDEBAR_WIDTH_EXPANDED
    : SIDEBAR_WIDTH_COLLAPSED;

  // Published globally (not on the layout root) so portaled surfaces —
  // entity drawers, the global search popup — can read it too.
  // Layout effect so the var is set before the first paint and surfaces
  // reading it never render a frame at the 0px fallback.
  React.useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      SIDEBAR_CSS_VAR,
      `${sidebarWidth}px`,
    );
    return () => {
      document.documentElement.style.removeProperty(SIDEBAR_CSS_VAR);
    };
  }, [sidebarWidth]);

  const value = React.useMemo(
    () => ({
      mode,
      toggleMode,
      isMobile,
      mobileOpen,
      openMobile,
      closeMobile,
      sidebarWidth,
    }),
    [
      mode,
      toggleMode,
      isMobile,
      mobileOpen,
      openMobile,
      closeMobile,
      sidebarWidth,
    ],
  );

  return (
    <SidebarStateContext.Provider value={value}>
      {children}
    </SidebarStateContext.Provider>
  );
};
